import { GERMAN_STATES } from '#lib/constants/germanStates';

const stateCodeByName = new Map(GERMAN_STATES.map((state) => [state.label.toLowerCase(), state.code]));

const stateCode = (location) =>
	GERMAN_STATES.some((state) => state.code === location.name)
		? location.name
		: stateCodeByName.get(location.name.toLowerCase());

/**
 * The assistant's search profile as a patch of the search mask's filters ("In Suchmaske
 * öffnen"). Location types follow the profile contract (`city`, `state`, `postalCode`,
 * `country`); a city wins over states, states over postal codes, as the mask knows one mode.
 * What the mask cannot hold — free-text criteria, an unknown state name, a second location
 * kind — is returned as `dropped`, so the caller can tell the user.
 *
 * @param {object|null} profile - `{ terms, locations, radius, employeesFrom, criteria }`
 * @param {object|null} mandate - `{ companyLimit }`
 * @returns {{patch: object, dropped: string[]}}
 */
export function profileToFilters(profile, mandate) {
	const patch = {};
	const dropped = [...(profile?.criteria ?? [])];
	if (profile?.terms?.length) patch.industrySearchTerms = profile.terms.join(', ');

	const locations = profile?.locations ?? [];
	const city = locations.find((location) => location.type === 'city');
	const states = locations.filter((location) => location.type === 'state');
	const postalCodes = locations.filter((location) => location.type === 'postalCode');
	if (city) {
		patch.locationMode = 'place';
		patch.location = city.name;
		if (profile.radius) patch.radius = profile.radius;
		dropped.push(...states.map((state) => state.name), ...postalCodes.map((code) => code.name));
	} else if (states.length) {
		const codes = states.map((state) => [state.name, stateCode(state)]);
		patch.locationMode = 'state';
		patch.states = codes.filter(([, code]) => code).map(([, code]) => code);
		dropped.push(
			...codes.filter(([, code]) => !code).map(([name]) => name),
			...postalCodes.map((code) => code.name),
		);
	} else if (postalCodes.length) {
		patch.locationMode = 'plz';
		patch.plzList = postalCodes.map((code) => code.name).join(', ');
	}

	if (profile?.employeesFrom) patch.employeesFrom = String(profile.employeesFrom);
	if (mandate?.companyLimit) patch.maxResults = mandate.companyLimit;
	return { patch, dropped };
}
